import { useRef } from 'react';
import { Button } from '@chakra-ui/react';
import { Upload } from 'lucide-react';
import { importAcadPgp, readAcadPgpFile, ACAD_IMPORT_PRESET_NAME } from '../../utils/acadPgp';
import { useAcadPgpStore } from '../../state/useAcadPgpStore';
import { notify } from '../../utils/notify';

/**
 * "Import acad.pgp" button for the Command Presets dialog. Reads an AutoCAD
 * Program Parameters file, resolves each alias to the equivalent canonical
 * command and registers the aliases as command-line names in the
 * "AutoCAD Import" preset (persisted; re-import replaces it in place).
 * acad.pgp carries command-line aliases only — no keyboard shortcuts.
 */
export function AcadPgpImportButton() {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImportFile = async (file: File) => {
        try {
            const text = await readAcadPgpFile(file);
            const summary = importAcadPgp(text);
            useAcadPgpStore.getState().setImport({
                fileName: file.name,
                entries: summary.applied.map((a) => ({ alias: a.alias, canonicalId: a.canonicalId })),
                extras: summary.legacyApplied,
            });
            const parts = [
                `${summary.presetAliases} alias(es) applied`,
                `${summary.presetCommands} command(s) renamed`,
            ];
            if (summary.legacyApplied.length > 0) {
                parts.push(`${summary.legacyApplied.length} file/clipboard alias(es) applied`);
            }
            if (summary.collisions.length > 0) parts.push(`${summary.collisions.length} shadowed built-in name(s)`);
            if (summary.skipped.length > 0) parts.push(`${summary.skipped.length} skipped`);
            notify(
                `acad.pgp imported: ${parts.join(', ')}${summary.presetAliases || summary.legacyApplied.length ? ` (preset "${ACAD_IMPORT_PRESET_NAME}" ${summary.presetCreated ? 'created' : 'updated'})` : ''}.`,
                summary.applied.length + summary.legacyApplied.length > 0 ? 'success' : 'warning'
            );
            const unmapped = summary.skipped.filter((s) => s.reason === 'unmapped-command');
            if (unmapped.length > 0) {
                console.warn(
                    '[AcadPgp] AutoCAD commands without an app equivalent:',
                    unmapped.map((s) => `${s.alias} → ${s.command} (line ${s.line})`)
                );
            }
            for (const collision of summary.collisions) {
                console.warn(
                    `[AcadPgp] Alias '${collision.alias}' shadows the built-in name of '${collision.shadowedLabel}'.`
                );
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error.';
            notify(`acad.pgp import failed: ${message}`, 'error');
        }
    };

    return (
        <>
            <Button
                size="xs"
                variant="outline"
                leftIcon={<Upload size={12} />}
                w="100%"
                justifyContent="flex-start"
                onClick={() => fileInputRef.current?.click()}
            >
                Import acad.pgp
            </Button>
            <input
                ref={fileInputRef}
                type="file"
                accept=".pgp,.txt,text/plain"
                className="hidden-file-input"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleImportFile(file);
                    e.target.value = '';
                }}
            />
        </>
    );
}
